import {
  ArrowDropDown,
  CheckBox,
  CheckBoxOutlineBlank,
  ExpandMore,
  TextFields,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  ButtonGroup,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import EditableTextField from 'components/editableTextField';
import { InputConfigurationProvider } from 'components/Input/InputConfigurationProvider';
import { FinsightEntityFieldValue } from 'components/renderField';
import useIsMobile from 'hooks/useIsMobile';
import React, { useState } from 'react';
import { FinsightEntity, FinsightEntityCreateRequest } from 'shared';
import { formatName } from 'utils/fields';

import { EntityCreateOptions } from './index.controller';

type CreateEntityFormProps = {
  entity: FinsightEntityCreateRequest;
  parent?: FinsightEntity;
  entityTypes: { data?: Array<{ type: string }> };
  requiredFields: Array<any>;
  optionalFields: Array<any>;
  loading: boolean;
  canSubmit: boolean;
  modalCtx?: { closeModal: () => void } | null;
  onEntityChange: (entity: FinsightEntityCreateRequest) => void;
  onOpenTextInput: () => void;
  onCreateEntity: (options: EntityCreateOptions) => void;
};

export function CreateEntityForm({
  entity,
  parent,
  entityTypes,
  requiredFields,
  optionalFields,
  loading,
  canSubmit,
  modalCtx,
  onEntityChange,
  onOpenTextInput,
  onCreateEntity,
}: CreateEntityFormProps) {
  const isMobile = useIsMobile();
  const [moreMenuAnchor, setMoreMenuAnchor] = useState<null | HTMLElement>(
    null,
  );
  const [autofillWithDataMaid, setAutofillWithDataMaid] = useState(false);

  return (
    <>
      <Stack
        gap={1}
        py={1}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <Stack gap={1} direction="row" alignItems="center">
          <Typography variant="h5">New</Typography>
          <EditableTextField
            options={entityTypes.data?.map((x) => ({
              value: x.type,
            }))}
            value={entity.type}
            variant="minimal"
            getLabel={(v) => formatName(v)}
            onChange={async (e) => onEntityChange({ ...entity, type: e })}
            sx={{
              width: 'unset',
              '& .MuiInputBase-root:not(#a):not(:hover)::before': {
                borderBottom: '1px dashed white',
              },
            }}
            InputProps={{
              sx: {
                fontSize: 'h5.fontSize',
                lineHeight: '1em',
              },
            }}
          />
        </Stack>
        <IconButton
          onClick={onOpenTextInput}
          title="Load from text input"
          sx={{ color: 'primary.main' }}
        >
          <TextFields />
        </IconButton>
      </Stack>

      <Typography>{parent ? parent.name : 'Root'}</Typography>

      <InputConfigurationProvider
        deferWrapper={{
          enabled: false,
        }}
      >
        <Stack direction={isMobile ? 'column' : 'row'} gap={2}>
          <Stack flexGrow={1} gap={2}>
            <TextField
              value={entity.name}
              label="Name"
              variant="outlined"
              fullWidth
              onChange={(e) =>
                onEntityChange({
                  ...entity,
                  name: e.target.value,
                })
              }
            />
            <TextField
              multiline
              minRows={4}
              value={entity.description}
              label="Description"
              variant="outlined"
              fullWidth
              onChange={(e) =>
                onEntityChange({
                  ...entity,
                  description: e.target.value,
                })
              }
            />

            {/* Show only required fields in the main area */}
            {requiredFields.map((field) => (
              <FinsightEntityFieldValue
                viewType="entity_main"
                editable
                source="create_modal"
                field={field}
                key={field.name}
                value={entity.extraFields?.[field.name] ?? null}
                onChange={async (v) =>
                  onEntityChange({
                    ...entity,
                    extraFields: {
                      ...entity.extraFields,
                      [field.name]: v,
                    },
                  })
                }
              />
            ))}
          </Stack>
          {!!requiredFields.length && (
            <Stack flex="0 0 300px" gap={2}>
              {requiredFields.map((field) => (
                <FinsightEntityFieldValue
                  viewType="entity_sidebar"
                  source="create_modal"
                  editable
                  field={field}
                  key={field.name}
                  value={entity.extraFields?.[field.name] ?? null}
                  onChange={async (v) =>
                    onEntityChange({
                      ...entity,
                      extraFields: {
                        ...entity.extraFields,
                        [field.name]: v,
                      },
                    })
                  }
                />
              ))}
            </Stack>
          )}
        </Stack>

        {/* Show all optional fields in an accordion */}
        {optionalFields.length > 0 && (
          <Accordion
            variant="outlined"
            sx={{
              backgroundColor: 'transparent',
              '& .MuiAccordionSummary-root': {
                backgroundColor: 'transparent',
              },
              '& .MuiAccordionDetails-root': {
                backgroundColor: 'transparent',
              },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography>Optional Fields ({optionalFields.length})</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack direction={isMobile ? 'column' : 'row'} gap={2}>
                <Stack flexGrow={1} gap={2}>
                  {optionalFields.map((field) => (
                    <FinsightEntityFieldValue
                      viewType="entity_main"
                      editable
                      source="create_modal"
                      field={field}
                      key={field.name}
                      value={entity.extraFields?.[field.name] ?? null}
                      onChange={async (v) =>
                        onEntityChange({
                          ...entity,
                          extraFields: {
                            ...entity.extraFields,
                            [field.name]: v,
                          },
                        })
                      }
                    />
                  ))}
                </Stack>
                <Stack flex="0 0 300px" gap={2}>
                  {optionalFields.map((field) => (
                    <FinsightEntityFieldValue
                      viewType="entity_sidebar"
                      source="create_modal"
                      editable
                      field={field}
                      key={field.name}
                      value={entity.extraFields?.[field.name] ?? null}
                      onChange={async (v) =>
                        onEntityChange({
                          ...entity,
                          extraFields: {
                            ...entity.extraFields,
                            [field.name]: v,
                          },
                        })
                      }
                    />
                  ))}
                </Stack>
              </Stack>
            </AccordionDetails>
          </Accordion>
        )}
      </InputConfigurationProvider>
      <Stack direction="row" justifyContent="flex-end">
        <Button color="error" onClick={() => modalCtx?.closeModal()}>
          Cancel
        </Button>
        <ButtonGroup variant="outlined">
          <Button
            disabled={loading || !canSubmit}
            onClick={() => onCreateEntity({ autofillWithDataMaid })}
          >
            Create
          </Button>
          <Button
            disabled={loading || !canSubmit}
            startIcon={<ArrowDropDown />}
            onClick={(e) => setMoreMenuAnchor(e.currentTarget)}
            sx={{
              p: 0,
              minWidth: '0 !important',
              m: 0,
              '& .MuiButton-icon': {
                m: 0,
              },
            }}
          />
          )
        </ButtonGroup>
        <Menu
          anchorEl={moreMenuAnchor}
          open={Boolean(moreMenuAnchor)}
          onClose={() => setMoreMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <MenuItem
            onClick={() => {
              setAutofillWithDataMaid(!autofillWithDataMaid);
            }}
          >
            <Stack direction="row" alignItems="center" gap={1}>
              {autofillWithDataMaid ? <CheckBox /> : <CheckBoxOutlineBlank />}
              <ListItemText primary="Autofill with DataMaid on create" />
            </Stack>
          </MenuItem>
        </Menu>
      </Stack>
    </>
  );
}
